import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ApiService from './../network/ApiService';
import PageHeader from '@layout/PageHeader';
import styles from '@widgets/PlayerKaderInfo/Kader.module.scss';
import AuthService from "../network/AuthService";
import styles2 from './TransferPlayers.module.css'; // Corrected path
import TransferCell from "@widgets/TransferCell/TransferCell";
import useTransferWindow from "../hooks/useTransferWindow";

const TransferPlayers = () => {
    const [players, setPlayers] = useState([]);
    const [team, setTeam] = useState(null); // State for team information
    const [loading, setLoading] = useState(true); // State for loading
    const [searchTerm, setSearchTerm] = useState(''); // State for search input
    const [errorMessage, setErrorMessage] = useState('');
    const { isOpen, loading: windowLoading, refresh: refreshTransferWindow } = useTransferWindow();
    const apiService = useMemo(() => new ApiService(), []);
    const authService = useMemo(() => new AuthService(), []);

    const teamID = authService.getTeamID();

    // Function to fetch all players data
    const fetchPlayersData = useCallback(async () => {
        setLoading(true);
        setErrorMessage('');
        try {
            const data = await apiService.get(`app/transfer/options/${teamID}`);
            setPlayers(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error("Failed fetching players:", error);
            setPlayers([]);
            setErrorMessage(error.message || 'Spieler konnten nicht geladen werden.');
        } finally {
            setLoading(false);
        }
    }, [apiService, teamID]);

    // Function to fetch team data
    const fetchTeamData = useCallback(async () => {
        if (teamID) {
            try {
                const teamData = await apiService.get(`app/team/${teamID}`);
                setTeam(teamData);
            } catch (error) {
                console.error("Error fetching team data:", error);
            }
        } else {
            console.error("No team ID found in cookies.");
        }
    }, [apiService, teamID]);

    useEffect(() => {
        if (!windowLoading && isOpen) {
            fetchTeamData();
            fetchPlayersData();
        } else if (!windowLoading) {
            setPlayers([]);
            setLoading(false);
        }
    }, [fetchPlayersData, fetchTeamData, isOpen, windowLoading]);

    // Function to fetch players by team name
    const fetchPlayersByName = async (searchValue) => {
        setLoading(true); // Set loading state when fetching data
        setErrorMessage('');
        try {
            const transferWindowIsOpen = await refreshTransferWindow();
            if (!transferWindowIsOpen) {
                setPlayers([]);
                return;
            }
            const options = await apiService.get(`app/transfer/options/${teamID}`);
            const query = searchValue.trim().toLocaleLowerCase('de-AT');
            setPlayers((Array.isArray(options) ? options : []).filter((player) =>
                player.name?.toLocaleLowerCase('de-AT').includes(query)
            ));
        } catch (error) {
            console.error("Error fetching players by name:", error);
            setPlayers([]);
            setErrorMessage(error.message || 'Die Suche ist fehlgeschlagen.');
        } finally {
            setLoading(false); // Stop loading after search
        }
    };

    // Handle search input submission
    const handleSearchSubmit = (event) => {
        event.preventDefault();
        if (searchTerm.trim()) {
            fetchPlayersByName(searchTerm);
        } else {
            fetchPlayersData(); // Fetch all players if the search term is empty
        }
    };

    return (
        <>
            <PageHeader title="Transfer Möglichkeiten" />
            {windowLoading || loading ? (
                <div className="spinner" />
            ) : !isOpen ? (
                <div className={styles2.closedState} role="status">
                    <div className={styles2.closedIcon} aria-hidden="true">🔒</div>
                    <h2>Transferfenster geschlossen</h2>
                    <p>Aktuell können keine Spieler gesucht und keine Transferanfragen erstellt werden.</p>
                    <p>Diese Ansicht öffnet automatisch wieder, sobald der ÖKFB den Transfermarkt freigibt.</p>
                </div>
            ) : (
                <>
                    <div style={{ backgroundColor: "#24292B", padding: "25px", marginBottom: "10px" }}>
                        <div className={styles2.lightH}>
                            Nur Spieler mit einer aktualisierten Email Adresse sind für Transfers berechtigt:
                        </div>
                        <div className={styles2.infoText}>
                            Bitte die Email Adressen der Spieler ihrer Mannschaft Aktualisieren
                        </div>
                    </div>
                    {/* Search Bar */}
                    <form style={{ padding: "10px 25px" }} onSubmit={handleSearchSubmit}>
                        <input
                            className="field"
                            type="search"
                            placeholder="Suchen sie nach Spielern bei Namen..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <button className="btn" type="submit">
                            Suchen
                        </button>
                    </form>
                    {errorMessage && <div className={styles2.errorMessage}>{errorMessage}</div>}
                    <div className={styles.kaderGrid}>
                        {/* Render TransferCell only if team data is available */}
                        {team && players.map((player) => (
                            <TransferCell
                                key={player.id}
                                player={player}
                                team={team}
                                refreshTransferWindow={refreshTransferWindow}
                            />
                        ))}
                        {/* Add a message if no players match the search criteria */}
                        {team && players.length === 0 && (
                            <div style={{ color: "white", textAlign: "center" }}>
                                Keine Spieler gefunden.
                            </div>
                        )}
                        {/* Add a message if team data is not yet loaded */}
                        {!team && <div>Loading team data...</div>}
                    </div>
                </>
            )}
        </>
    );
};

export default TransferPlayers;
